import { Component, createRef, type ReactNode } from 'react';
import './dealerHandoff.css';
import { DEALER_HANDOFF_EVENT } from './RoundCompletion';
import { screenRect } from '../../helpers/utils/scrollFrame';

type Props = { count: number; children: ReactNode; deferRoundMove?: boolean };
type FlightGate = { ready: boolean; animation?: Animation; remove: () => void };
type Departure = {
  left: number;
  top: number;
  width: number;
  height: number;
  dealerId: string | null;
  completedRound: boolean;
};

/**
 * Where `position:fixed; top/left: 0` paints, in viewport coordinates.
 * That is the origin on a desktop page. A phone's visual viewport can sit
 * below the layout viewport (toolbar, keyboard), so a chip fixed at a
 * bounding rect rides high and then jumps down when the real badge shows.
 */
function fixedViewportOrigin(): { x: number; y: number } {
  const probe = document.createElement('div');
  probe.style.cssText =
    'position:fixed;top:0;left:0;width:0;height:0;margin:0;padding:0;border:0;pointer-events:none;visibility:hidden';
  document.body.appendChild(probe);
  const rect = probe.getBoundingClientRect();
  probe.remove();
  return { x: rect.left, y: rect.top };
}

/** Snapshot before React replaces the board: a layout effect is already too late. */
export default class DealerHandoff extends Component<Props> {
  private root = createRef<HTMLDivElement>();
  private stopFlight?: () => void;

  getSnapshotBeforeUpdate(previous: Props): Departure | null {
    const completedRound = this.props.count === previous.count + 1;
    if (
      (!completedRound && this.props.count !== previous.count) ||
      window.matchMedia('(prefers-reduced-motion: reduce)').matches
    )
      return null;
    const badge = this.root.current?.querySelector(
      '[data-testid="dealerBadge"]',
    );
    if (!badge) return null;
    const { left, top, width, height } = screenRect(badge);
    const dealerId =
      badge.closest('[data-deal-seat]')?.getAttribute('data-deal-seat') ?? null;
    return { left, top, width, height, dealerId, completedRound };
  }

  componentDidUpdate(
    _previous: Props,
    _state: unknown,
    from: Departure | null,
  ) {
    this.stopFlight?.();
    const badge = this.root.current?.querySelector<HTMLElement>(
      '[data-testid="dealerBadge"]',
    );
    if (!from || !badge || !badge.animate) return;
    const dealerId =
      badge.closest('[data-deal-seat]')?.getAttribute('data-deal-seat') ?? null;
    if (!from.completedRound && from.dealerId === dealerId) return;
    const visibility = badge.style.visibility;
    badge.style.visibility = 'hidden';
    let gate: FlightGate | undefined;
    if (from.completedRound && this.props.deferRoundMove) {
      const count = this.props.count;
      const release = (event: Event) => {
        if ((event as CustomEvent<number>).detail !== count || !gate) return;
        gate.ready = true;
        gate.animation?.play();
      };
      gate = {
        ready: false,
        remove: () => window.removeEventListener(DEALER_HANDOFF_EVENT, release),
      };
      // Listen before the measurement frame, including early cancellation.
      window.addEventListener(DEALER_HANDOFF_EVENT, release);
    }
    // Seat measurements run in layout effects on the new board. Wait until
    // those updates settle before choosing the exact landing position.
    const frame = requestAnimationFrame(() =>
      this.fly(from, badge, visibility, gate),
    );
    this.stopFlight = () => {
      cancelAnimationFrame(frame);
      gate?.remove();
      badge.style.visibility = visibility;
      this.stopFlight = undefined;
    };
  }

  private fly(
    from: Departure,
    badge: HTMLElement,
    visibility: string,
    gate?: FlightGate,
  ) {
    const to = screenRect(badge);
    const width = badge.offsetWidth;
    const height = badge.offsetHeight;
    if (!width || !height) {
      this.stopFlight?.();
      return;
    }
    const origin = fixedViewportOrigin();
    // Copy the rendered chip into viewport coordinates so stage zoom, the
    // board's entrance fade and Classic's shrinking Made row cannot distort it.
    const flight = document.createElement('span');
    const paint = getComputedStyle(badge);
    // Not the insets: style text lists inset-block and inset-inline after the
    // top and left set below, so a session recording would redraw the D at 0,0.
    for (const property of Array.from(paint))
      if (!property.startsWith('inset'))
        flight.style.setProperty(property, paint.getPropertyValue(property));
    flight.textContent = 'D';
    flight.dataset.testid = 'dealer-handoff';
    flight.setAttribute('aria-hidden', 'true');
    const start = `translate(${from.left - to.left}px, ${from.top - to.top}px) scale(${from.width / width}, ${from.height / height})`;
    const end = `translate(0, 0) scale(${to.width / width}, ${to.height / height})`;
    Object.assign(flight.style, {
      position: 'fixed',
      left: `${to.left - origin.x}px`,
      top: `${to.top - origin.y}px`,
      width: `${width}px`,
      height: `${height}px`,
      margin: '0',
      pointerEvents: 'none',
      zIndex: '1407',
      visibility: 'visible',
      // Paint at the departure if the animation's first frame is late.
      transform: start,
      transformOrigin: 'top left',
      zoom: '1',
    });
    document.body.appendChild(flight);
    const animation = flight.animate(
      [{ transform: start }, { transform: end }],
      { duration: 1200, easing: 'cubic-bezier(0.4, 0, 0.2, 1)', fill: 'both' },
    );
    const onViewport = () => {
      const next = fixedViewportOrigin();
      flight.style.left = `${parseFloat(flight.style.left) - (next.x - origin.x)}px`;
      flight.style.top = `${parseFloat(flight.style.top) - (next.y - origin.y)}px`;
      origin.x = next.x;
      origin.y = next.y;
    };
    window.visualViewport?.addEventListener('scroll', onViewport);
    window.visualViewport?.addEventListener('resize', onViewport);
    if (gate) {
      gate.animation = animation;
      if (!gate.ready) animation.pause();
    }
    const cleanup = () => {
      gate?.remove();
      window.visualViewport?.removeEventListener('scroll', onViewport);
      window.visualViewport?.removeEventListener('resize', onViewport);
      animation.onfinish = null;
      animation.oncancel = null;
      animation.cancel();
      flight.remove();
      badge.style.visibility = visibility;
      window.removeEventListener('resize', cleanup);
      window.removeEventListener('scroll', cleanup, true);
      this.stopFlight = undefined;
    };
    this.stopFlight = cleanup;
    animation.onfinish = () => {
      cleanup();
      if (from.completedRound) this.dealAfterLanding(badge);
    };
    animation.oncancel = cleanup;
    window.addEventListener('resize', cleanup);
    window.addEventListener('scroll', cleanup, true);
  }

  private dealAfterLanding(badge: HTMLElement) {
    const cards: HTMLElement[] = [];
    const animations: Animation[] = [];
    let timer = 0;
    const cleanup = () => {
      window.clearTimeout(timer);
      for (const animation of animations) {
        animation.onfinish = null;
        animation.oncancel = null;
        animation.cancel();
      }
      for (const card of cards) card.remove();
      window.removeEventListener('resize', cleanup);
      window.removeEventListener('scroll', cleanup, true);
      this.stopFlight = undefined;
    };
    timer = window.setTimeout(() => {
      if (
        !badge.isConnected ||
        window.matchMedia('(prefers-reduced-motion: reduce)').matches
      ) {
        cleanup();
        return;
      }
      const seats = [
        ...(this.root.current?.querySelectorAll<HTMLElement>(
          '[data-deal-seat]',
        ) ?? []),
      ];
      if (seats.length !== 4) {
        cleanup();
        return;
      }
      const from = screenRect(badge);
      const origin = fixedViewportOrigin();
      const scale = from.height / badge.offsetHeight || 1;
      const x = from.left + from.width / 2;
      const y = from.top + from.height / 2;
      const centerOf = (seat: HTMLElement) => {
        const rect = screenRect(seat.querySelector('[data-seat-card]') ?? seat);
        return { x: rect.left + rect.width / 2, y: rect.top + rect.height / 2 };
      };
      const centers = seats.map(centerOf);
      const centerX =
        centers.reduce((sum, point) => sum + point.x, 0) / seats.length;
      const centerY =
        centers.reduce((sum, point) => sum + point.y, 0) / seats.length;
      const angleOf = (seat: HTMLElement) => {
        const point = centerOf(seat);
        return Math.atan2(point.y - centerY, point.x - centerX);
      };
      const dealer = seats.find((seat) => seat.contains(badge))!;
      const dealerAngle = angleOf(dealer);
      // Screen Y grows downward: increasing angles move clockwise.
      const distance = (seat: HTMLElement) =>
        (angleOf(seat) - dealerAngle + 2 * Math.PI) % (2 * Math.PI);
      const recipients = seats
        .filter((seat) => seat !== dealer)
        .sort((a, b) => distance(a) - distance(b));
      let remaining = recipients.length;
      const sendCard = (index: number) => {
        if (
          !badge.isConnected ||
          window.matchMedia('(prefers-reduced-motion: reduce)').matches ||
          index === recipients.length
        ) {
          cleanup();
          return;
        }
        const seat = recipients[index];
        const target = seat.querySelector('[data-seat-card]') ?? seat;
        const to = screenRect(target);
        const card = document.createElement('span');
        card.className = 'dealer-playing-card';
        card.dataset.testid = 'dealer-card';
        card.dataset.recipient = seat.dataset.dealSeat;
        card.setAttribute('aria-hidden', 'true');
        card.textContent = '♠';
        card.style.left = `${x - 13 * scale - origin.x}px`;
        card.style.top = `${y - 18 * scale - origin.y}px`;
        card.style.width = `${26 * scale}px`;
        card.style.height = `${36 * scale}px`;
        card.style.fontSize = `${18 * scale}px`;
        document.body.appendChild(card);
        cards.push(card);
        const dx = to.left + to.width / 2 - x;
        const dy = to.top + to.height / 2 - y;
        const rotation = [-12, 8, -6][index];
        const animation = card.animate(
          [
            {
              transform: 'translate(0, 0) rotate(0deg)',
              opacity: 1,
              offset: 0,
            },
            { opacity: 1, offset: 0.8 },
            {
              transform: `translate(${dx}px, ${dy}px) rotate(${rotation}deg)`,
              opacity: 0,
              offset: 1,
            },
          ],
          {
            duration: 700,
            easing: 'cubic-bezier(0.22, 0.61, 0.36, 1)',
            fill: 'both',
          },
        );
        animation.onfinish = () => {
          card.remove();
          if (--remaining === 0) cleanup();
        };
        animation.oncancel = cleanup;
        animations.push(animation);
        if (index + 1 < recipients.length) {
          timer = window.setTimeout(() => sendCard(index + 1), 250);
        }
      };
      sendCard(0);
    }, 200);
    this.stopFlight = cleanup;
    window.addEventListener('resize', cleanup);
    window.addEventListener('scroll', cleanup, true);
  }

  componentWillUnmount() {
    this.stopFlight?.();
  }

  render() {
    return <div ref={this.root}>{this.props.children}</div>;
  }
}
